import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { BarChart3, Shield, AlertTriangle, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIP_LABELS = {
  asansor: 'Asansör', jenerator: 'Jeneratör', ups: 'UPS', trafo: 'Trafo',
  yangin_algilama: 'Yangın Alg.', sprinkler: 'Sprinkler', duman_tahliye: 'Duman Tah.',
  hvac_klima: 'HVAC', buhar_kazani: 'Buhar Kaz.', topraklama: 'Topraklama',
  paratoner: 'Paratoner', yapi_iskelesi: 'İskele',
  kompanzasyon: 'Kompanzasyon', boyler: 'Boyler', genlesme_tanki: 'Genleşme T.',
  kazan: 'Kazan', brulor: 'Brülör', kum_filtresi: 'Kum Filtresi', diger: 'Diğer'
};

const CHART_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];
const PIE_COLORS = ['#10B981', '#EF4444'];

export default function PKRaporlama() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/pk/raporlama/ozet`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;
  if (!data) return <div className="text-red-400 text-center py-8">Veriler yüklenemedi</div>;

  const arizaChartData = data.ariza_sikligi.map(a => ({ name: TIP_LABELS[a.tip] || a.tip, sayi: a.sayi }));

  const uyumPieData = [
    { name: 'Zamanında', value: data.kontrol_uyum.zamaninda },
    { name: 'Geciken', value: data.kontrol_uyum.geciken }
  ];

  return (
    <div className="space-y-6" data-testid="pk-raporlama">
      <h2 className="text-xl font-bold text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-400" /> Raporlama & Analitik</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ekipman Bazlı Arıza Sıklığı */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Ekipman Tipine Göre Arıza Sıklığı</h3>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={arizaChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#D1D5DB', fontSize: 11 }} width={85} />
                  <Tooltip contentStyle={{ background: '#1F2937', border: '1px solid #374151', color: '#fff', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="sayi" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Periyodik Kontrol Uyum Oranı */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-green-400" /> Periyodik Kontrol Uyum Oranı</h3>
            <div className="flex items-center gap-8">
              <div className="h-[200px] w-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={uyumPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                      {uyumPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#1F2937', border: '1px solid #374151', color: '#fff', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#9CA3AF' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-400">{data.kontrol_uyum.uyum_orani}%</p>
                  <p className="text-xs text-gray-400">Uyum Oranı</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div><p className="text-lg font-bold text-green-400">{data.kontrol_uyum.zamaninda}</p><p className="text-[10px] text-gray-500">Zamanında</p></div>
                  <div><p className="text-lg font-bold text-red-400">{data.kontrol_uyum.geciken}</p><p className="text-[10px] text-gray-500">Geciken</p></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Firma SLA Performansı */}
        <Card className="bg-gray-900/80 border-gray-700/50 lg:col-span-2">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-400" /> Firma SLA Performansı</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700 text-xs text-gray-400">
                    <th className="text-left py-2 px-3">Firma</th>
                    <th className="text-center py-2 px-3">Toplam Arıza</th>
                    <th className="text-center py-2 px-3">SLA İhlali</th>
                    <th className="text-center py-2 px-3">İhlal Oranı</th>
                    <th className="text-center py-2 px-3">Ort. Müdahale</th>
                    <th className="text-center py-2 px-3">Performans</th>
                  </tr>
                </thead>
                <tbody>
                  {data.firma_performans.map((f, i) => (
                    <tr key={i} className="border-b border-gray-800/50">
                      <td className="py-2 px-3 text-white">{f.firma}</td>
                      <td className="py-2 px-3 text-center text-gray-300">{f.toplam_ariza}</td>
                      <td className="py-2 px-3 text-center text-red-400">{f.sla_ihlal}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={f.ihlal_orani > 20 ? 'text-red-400' : f.ihlal_orani > 10 ? 'text-orange-400' : 'text-green-400'}>
                          %{f.ihlal_orani}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center text-gray-300">{f.ort_mudahale_saat}h</td>
                      <td className="py-2 px-3 text-center">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div className={`h-2 rounded-full ${f.ihlal_orani > 20 ? 'bg-red-500' : f.ihlal_orani > 10 ? 'bg-orange-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.max(5, 100 - f.ihlal_orani)}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Maliyet Analizi (Son 6 Ay) */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Aylık Ceza Kesintileri (Son 6 Ay)</h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.maliyet_analizi} margin={{ left: 5, right: 5 }}>
                  <XAxis dataKey="ay" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#1F2937', border: '1px solid #374151', color: '#fff', borderRadius: 8, fontSize: 12 }} formatter={v => [`${v.toLocaleString('tr-TR')} TL`, 'Ceza']} />
                  <Bar dataKey="ceza" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bina Risk Skoru */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-400" /> Bina Bazlı Risk Skoru</h3>
            <div className="space-y-3">
              {data.bina_risk.map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{b.bina}</p>
                    <div className="flex gap-2 mt-1">
                      {b.kirmizi > 0 && <Badge className="bg-red-800 text-[10px]">{b.kirmizi} kırmızı</Badge>}
                      {b.geciken > 0 && <Badge className="bg-orange-800 text-[10px]">{b.geciken} geciken</Badge>}
                      {b.acik_ariza > 0 && <Badge className="bg-yellow-800 text-[10px] text-yellow-200">{b.acik_ariza} arıza</Badge>}
                      {b.skor === 0 && <Badge className="bg-green-800 text-[10px]">Güvenli</Badge>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${b.skor > 50 ? 'text-red-400' : b.skor > 20 ? 'text-orange-400' : 'text-green-400'}`}>{b.skor}</p>
                    <p className="text-[10px] text-gray-500">Risk Puanı</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
